import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePricingStore } from '../stores/pricing.store';
import { logger } from '@/features/help/utils/logger';

const businessFeatures = [
  'Unlimited submissions',
  'Custom field mapping',
  'Priority support',
  'Custom branding',
  'CRM integration',
  'PDF report generation',
  'Custom Form Fields',
];

const customFeatures = [
  'All in Design & Development',
  'Advanced customization',
  'Priority support',
  'Everything in Basic',
];

// Format price based on type (base price has no decimals, metered prices show 2 decimals)
const formatPrice = (price: number, isMetered: boolean = false) => {
  return isMetered ? price.toFixed(2) : Math.round(price);
};

// Add loading skeleton component
const PricingSkeleton = () => (
  <div className="py-24 bg-gray-50">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center">
        <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mx-auto"></div>
        <div className="mt-4 h-6 w-96 bg-gray-200 rounded animate-pulse mx-auto"></div>
      </div>
      <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {[1, 2].map((i) => (
          <div key={i} className="bg-white rounded-2xl p-8 border border-gray-200">
            <div className="h-6 w-24 bg-gray-300 rounded animate-pulse"></div>
            <div className="mt-4 h-10 w-32 bg-gray-300 rounded animate-pulse"></div>
            <div className="mt-6 space-y-3">
              {[1, 2, 3, 4].map((j) => (
                <div key={j} className="h-4 w-full bg-gray-300 rounded animate-pulse"></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const Pricing = () => {
  const { basePrice, standardRate, isLoading, fetchPricing } = usePricingStore();
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    logger.info('Pricing', 'Pricing component mounted', {}, 'pricing-component-mount');
    fetchPricing();
  }, [fetchPricing]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.05, rootMargin: '150px 0px' }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    // Fallback timeout to ensure component shows up
    const fallbackTimer = setTimeout(() => {
      setIsVisible(true);
    }, 2000);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      clearTimeout(fallbackTimer);
    };
  }, []);

  if (isLoading) {
    return <PricingSkeleton />;
  }

  return (
    <div id="pricing" className="py-20 bg-gray-50" ref={ref}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transition-all duration-700 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Pay based on how your applicants submit their information
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Business Plan */}
          <div className={`bg-white rounded-2xl p-8 border-2 border-blue-300 shadow-xl shadow-blue-200/30 ring-1 ring-blue-200/20 relative before:absolute before:inset-0 before:rounded-2xl before:bg-gradient-to-r before:from-blue-500/10 before:to-blue-300/5 before:blur-2xl before:-z-10 flex flex-col h-full transition-all duration-600 ease-out delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}>
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-blue-500 mb-2">Business</h3>
              <div className="flex items-baseline mb-4">
                <span className="text-5xl font-bold text-blue-500">${formatPrice(basePrice)}</span>
                <span className="text-gray-600 ml-2">/month base price</span>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-600">
                  <span className="text-lg font-semibold text-blue-500">+${formatPrice(standardRate, true)}</span> per submitted application
                </p>
              </div>
            </div>

            <ul className="space-y-3 mb-8 flex-grow">
              {businessFeatures.map((feature) => (
                <li key={feature} className="flex items-start">
                  <Check className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto">
              <Link
                to="/signup"
                className="w-full bg-blue-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-600 transition-colors text-center block"
              >
                Get Started →
              </Link>
            </div>
          </div>

          {/* Custom Plan */}
          <div className={`bg-white rounded-2xl p-8 border-2 border-gray-300 shadow-lg flex flex-col h-full transition-all duration-600 ease-out delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}>
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Custom</h3>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Let's Talk!</h4>
              
              <div className="mb-6">
                <p className="text-gray-900 font-semibold mb-2">Everything You Need</p>
                <p className="text-gray-600">
                  Customize a plan based on your specific needs.
                </p>
              </div>
            </div>

            <ul className="space-y-3 mb-8 flex-grow">
              {customFeatures.map((feature) => (
                <li key={feature} className="flex items-start">
                  <Check className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto">
              <Link
                to="/signup"
                className="w-full bg-blue-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-600 transition-colors text-center block"
              >
                Get Started →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
